import { useState, useEffect, useRef } from 'react';
import MojitoCore from 'mojito/core';
import defaultConfig from 'mojito/core/generated/configs/_postponed-renderer.js';

const IdGenerator = MojitoCore.Base.IdGenerator;
const { useConfig, useAppContext } = MojitoCore.Presentation.Hooks;
MojitoCore.Services.Config.configRegistry.add(defaultConfig);

/**
 * Component used to render children asynchronously and with a possible delay.
 *
 * @example <caption>Delayed rendering<br><br>Can be used to delay rendering certain large components in order to free up execution time for more time-critical tasks. A parent component may render a handful of children, but child X is not as critical as the others. By wrapping child X in PostponedRenderer, the initialization and rendering of X will be part of a new task. This task will not execute until after the current ask is finished, letting the other children initialize and render first.</caption>
 *  render() {
 *      return (
 *          <>
 *              <PostponedRenderer delay={500}>
 *                  <Child/>
 *              </PostponedRenderer>
 *              <CriticalChild/>
 *          </>
 *      );
 *  }
 *
 *
 * @example <caption>Speeding up side-effects<br><br>If a component needs to speed up execution of functions with side-effects, wrapping all children in PostponedRenderer will execute that side-effect before rendering the children, given that the side-effect can be executed at the time. If, given the code below, <code>doSomething</code> needs to be executed asynchronously but before <code>Child</code> is rendered, this is how it can be used.</caption>
 *  class ExampleView extends UIViewImplementation
 *      constructor() {
 *          setTimeout(() => {
 *              doSomething()
 *          })
 *      }
 *
 *      render() {
 *          return (
 *              <PostponedRenderer>
 *                  <Child/>
 *              </PostponedRenderer>
 *          );
 *      }
 *  }
 *
 * @function PostponedRenderer
 * @memberof Mojito.Presentation.Components.PostponedRenderer
 *
 * @param {*} props - Represents the properties passed to the component.
 * @param {number} props.delay - Determines the amount of delay, in milliseconds, before rendering the children. The default value is 0.
 * @param {number} props.priority - Determines the priority of a child component in rendering queue. The default value is 0.
 * @param {node} props.children - Specifies the children elements to be rendered after the delay.
 * @param {string} [props.name] - Component name, used for debugging.
 * @param {boolean} [props.disabled] - If set to true, the component will not postpone rendering.
 * @returns {React.ReactElement|node} Returns a React element prepared for rendering.
 */

const PostponedRenderer = ({ children, priority = 0, delay = 0, name, disabled = false }) => {
    const [postponedChildren, setPostponedChildren] = useState(null);
    const wasPostponed = useRef(false);
    const config = useConfig('PostponedRenderer', undefined, useAppContext().uiContextPath);
    const skipPostponedRendering = config?.disabled || disabled;

    useEffect(() => {
        if (!children || wasPostponed.current || skipPostponedRendering) {
            return;
        }
        const task = { children, delay, priority, name, id: IdGenerator.generateId() };
        renderQueue.addToQueue(task).then(node => {
            setPostponedChildren(node);
            wasPostponed.current = true;
        });

        return () => {
            renderQueue.removeFromQueue(task.id);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [children]);

    return wasPostponed.current || skipPostponedRendering ? children : postponedChildren;
};

/**
 * Class that provides a queue for managing render tasks with delays.
 * Ensures that tasks are processed sequentially with specified timeouts.
 *
 * @class RenderQueue
 * @name renderQueue
 * @memberof Mojito.Presentation.Components.PostponedRenderer
 * @private
 */
class RenderQueue {
    constructor() {
        this.renderQueue = [];
        this.isProcessing = false;
    }

    /**
     * Adds a new render task to the queue.
     *
     * @param {object} task - The render task to be added.
     * @param {node} task.children - The React component to render.
     * @param {number} task.delay - The delay in milliseconds before rendering the component.
     * @returns {Promise<node>} A promise that resolves with the child component when it's time to render.
     */
    addToQueue(task) {
        return new Promise(resolve => {
            this.renderQueue.push({
                ...task,
                order: this.renderQueue.length,
                resolve,
                canceled: false,
            });
            setTimeout(() => this.processQueue(), 0);
        });
    }

    /**
     * Removes task from queue and stops processing if there are no more tasks left.
     *
     * @param {string} taskId - The task id to be removed.
     */
    removeFromQueue(taskId) {
        const index = this.renderQueue.findIndex(task => task.id === taskId);
        if (index !== -1) {
            this.renderQueue[index].canceled = true;
            this.renderQueue.splice(index, 1);
        }
        if (this.renderQueue.length === 0) {
            this.isProcessing = false;
        }
    }

    /**
     * Processes the render queue.
     * Executes each task sequentially, respecting the specified delays.
     *
     * @private
     */
    async processQueue() {
        if (this.isProcessing || this.renderQueue.length === 0) {
            return;
        }

        this.isProcessing = true;

        while (this.renderQueue.length > 0) {
            this.renderQueue.sort((t1, t2) => t1.priority - t2.priority || t1.order - t2.order);
            const task = this.renderQueue.shift();
            const { children, delay, resolve } = task;
            if (task.canceled) {
                continue;
            }
            await new Promise(r => setTimeout(r, delay));

            if (!task.canceled) {
                resolve(children);
            }
        }

        this.isProcessing = false;
    }
}

const renderQueue = new RenderQueue();

export default PostponedRenderer;
